import React from 'react';
import ReactDOM from 'react-dom';
import useEscapeKey from '../hooks/useEscapeKey';
import './ConfirmationModal.css';

/**
 * ConfirmationModal - Lightweight iOS-style confirmation dialog
 *
 * Replaces browser native window.confirm() with branded, accessible modal
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {string} title - Bold heading text
 * @param {string} message - Explanation text (grey, smaller)
 * @param {string} confirmText - Confirm button label (default: "Confirm")
 * @param {string} cancelText - Cancel button label (default: "Cancel")
 * @param {function} onConfirm - Callback when confirm clicked
 * @param {function} onCancel - Callback when cancel clicked or backdrop clicked
 * @param {boolean} isProcessing - Disables buttons during async operations
 * @param {string} variant - Button color: 'danger' (red) | 'primary' (turquoise) | 'warning' (orange) | 'purple' (purple-900)
 * @param {string} [secondaryText] - Optional third action label (rendered between Cancel and Confirm). Enables a two-choice prompt.
 * @param {function} [onSecondary] - Callback when the secondary action is clicked
 * @param {string} [secondaryVariant='purple'] - Button colour for the secondary action
 */
const ConfirmationModal = ({
    isOpen,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    onConfirm,
    onCancel,
    isProcessing = false,
    variant = 'primary', // 'danger', 'primary', 'warning'
    maxWidth,
    secondaryText,
    onSecondary,
    secondaryVariant = 'purple',
    overlayZIndex,
}) => {
    // Escape cancels (unless an async action is mid-flight). The shared Escape
    // stack ensures that when this sits on top of a BaseModal, Escape closes
    // only this confirmation first — not both at once.
    useEscapeKey(isOpen, () => {
        if (!isProcessing) onCancel?.();
    });

    if (!isOpen) return null;

    const handleConfirm = () => {
        if (!isProcessing) {
            onConfirm();
        }
    };

    const handleCancel = () => {
        if (!isProcessing) {
            onCancel();
        }
    };

    const handleSecondary = () => {
        if (!isProcessing) {
            onSecondary?.();
        }
    };

    return ReactDOM.createPortal(
        <div
            className="confirmation-modal-overlay"
            onClick={handleCancel}
            style={overlayZIndex ? { zIndex: overlayZIndex } : undefined}
        >
            <div
                className="confirmation-modal"
                style={maxWidth ? { maxWidth } : undefined}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Content - Centered text */}
                <div className="confirmation-modal-content">
                    <h2 className="confirmation-modal-title">{title}</h2>
                    <p className="confirmation-modal-message">{message}</p>
                </div>

                {/* Buttons - Side by side */}
                <div className="confirmation-modal-buttons">
                    <button
                        onClick={handleCancel}
                        disabled={isProcessing}
                        className="confirmation-btn-cancel"
                    >
                        {cancelText}
                    </button>
                    {secondaryText && (
                        <button
                            onClick={handleSecondary}
                            disabled={isProcessing}
                            className={`confirmation-btn-confirm confirmation-btn-${secondaryVariant}`}
                        >
                            {secondaryText}
                        </button>
                    )}
                    <button
                        onClick={handleConfirm}
                        disabled={isProcessing}
                        className={`confirmation-btn-confirm confirmation-btn-${variant}`}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ConfirmationModal;
